﻿import React, { useState } from 'react';
import { RecentTimePeriodType } from '../../models/enums';
import { LogDatum } from '../../models/Logging/log-datum';
import { LogDataRequest } from '../../models/Logging/log-data-request';
import { CrudFunctions } from '../../utilities/crud-functions';
import GlobalDialog from '../Modal/GlobalDialog';
import { ModalMetadata } from '../../models/modal-metadata';
import { ModalMessageType } from '../../models/enums';

import DataGrid, {
    Column,
    Paging,
    ColumnFixing,
    Toolbar,
    Selection,
    HeaderFilter,
    FilterRow,
    Item as ToolBarItem,
    LoadPanel
} from 'devextreme-react/data-grid';

import SelectBox from 'devextreme-react/select-box';
import { Button } from 'devextreme-react/button';
import DateBox from 'devextreme-react/date-box';
import TextArea from 'devextreme-react/text-area';

const timePeriods = [
    { id: RecentTimePeriodType.Last5Minutes, period: "Last 5 minutes" },
    { id: RecentTimePeriodType.Last30Minutes, period: "Last 30 minutes" },
    { id: RecentTimePeriodType.LastHour, period: "Last hour" },
    { id: RecentTimePeriodType.Last12Hours, period: "Last 12 hours" },
    { id: RecentTimePeriodType.Last24Hours, period: "Last 24 hours" }
];

export default function LoggingInformationForm() {
    const [logData, setLogData] =
        useState<LogDatum[]>();

    const [selectedLogDatum, setSelectedLogDatum] =
        useState<LogDatum>();

    const [selectedTimePeriod, setSelectedTimePeriod] =
        useState<any>();

    const [beginSearchDate, setBeginSearchDate] =
        useState<Date>();

    const [endSearchDate, setEndSearchDate] =
        useState<Date>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const gridWidth = 'calc(90vw - 13em)';

    return (
        <div className="pw-form-container">
            <div className="lq-title-bar">
                <div className="lq-main-title">Logging Information</div>
                <div className="title-bar-spacer" />
                <div className="save-toolbar">
                    <div>
                        <Button className="title-bar-button"
                            id="goBackButton" text="" icon="arrowleft" type="normal"
                            hint="go back"
                            onClick={goBack} />
                    </div>
                </div>
            </div>

            <div className="pw-comp-row">
                <div className="pw-form-unit">
                    <Button id="getLogDataByRecentTimePeriodButton" text="" icon="clock" type="success"
                        disabled={!selectedTimePeriod} className="pw-row-button-08"
                        onClick={getLogDataByRecentTimePeriod} />
                </div>

                <div className="pw-form-unit pw-comp-20">
                    <div className="pw-form-unit-label">Recent Time Period</div>
                    <SelectBox id="recentTimePeriod"
                        className="selectbox"
                        displayExpr='period' valueExpr='id'
                        dataSource={timePeriods ? timePeriods : []}
                        onValueChanged={onTimePeriodSelectionChanged}
                        value={selectedTimePeriod ? selectedTimePeriod : null} />
                </div>
            </div>

            <div className="pw-comp-row">
                <div className="pw-form-unit">
                    <Button id="getLogDataByStartAndEndTimeButton" text="" icon="clock" type="success"
                        disabled={!(beginSearchDate && endSearchDate)} className="pw-row-button-11"
                        onClick={getLogDataByStartAndEndTime} />
                </div>

                <div className="pw-form-unit pw-comp-15">
                    <label className="pw-form-unit-label">Search Begin Date</label>
                    <DateBox id="beginSearchDate" type="date" displayFormat="MM/dd/yyyy"
                        showClearButton={true}
                        defaultValue={null}
                        onValueChanged={onDateValueChanged}
                        value={beginSearchDate ? beginSearchDate : undefined}
                        placeholder="date" />
                </div>

                <div className="pw-form-unit pw-comp-15">
                    <label className="pw-form-unit-label">Search End Date</label>
                    <DateBox id="endSearchDate" type="date" displayFormat="MM/dd/yyyy"
                        showClearButton={true}
                        defaultValue={null}
                        onValueChanged={onDateValueChanged}
                        value={endSearchDate ? endSearchDate : undefined}
                        placeholder="date" />
                </div>
            </div>

            <div id="logging-data-datagrid-container" className="pw-datagrid">
                <DataGrid keyExpr="id"
                    height="60vh" width={gridWidth}
                    dataSource={logData ? logData : []}
                    onSelectionChanged={onSelectionChanged}
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    showBorders={true} >

                    <HeaderFilter visible={true} />
                    <FilterRow visible={true} />
                    <LoadPanel enabled={true} />

                    <Selection mode='single' />

                    <LoadPanel enabled={true} />
                    <ColumnFixing enabled={true} />
                    <Paging enabled={false} />

                    <Column dataField='id' dataType="number" alignment='center' width='4rem' />
                    <Column dataField='level' width='5rem' />
                    <Column dataField='userName' width='10rem' />
                    <Column dataField='timeStamp' dataType="date" format="MM/dd/yyyy HH:mm:ss" width='10rem' />
                    <Column dataField='duration' dataType="number" width='6rem' />
                    <Column dataField='message' width='25rem' cellRender={messageItemRender} />
                    
                    <Toolbar>
                        <ToolBarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Log Data</div>
                            </div>
                        </ToolBarItem>

                    </Toolbar>
                </DataGrid>

            </div>

            <div className="pw-form-unit">
                <label className="pw-form-unit-label">Log Message</label>
                <TextArea
                    height="10rem"
                    value={selectedLogDatum && selectedLogDatum.message ?
                        selectedLogDatum.message : undefined}
                    autoResizeEnabled={true} />
            </div>
            <div className="pw-form-unit">
                <label className="pw-form-unit-label">Error Information</label>
                <TextArea
                    height="10rem"
                    value={selectedLogDatum && selectedLogDatum.exception ?
                        selectedLogDatum.exception : undefined}
                    autoResizeEnabled={true} />
            </div>

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }

        </div>
    );

    function getLogDataByRecentTimePeriod() {
        if (!selectedTimePeriod)
            return;

        const now = new Date();
        let minutes = 0;

        switch (selectedTimePeriod) {
            case RecentTimePeriodType.Last5Minutes:
                minutes = 5;
                break;
            case RecentTimePeriodType.Last30Minutes:
                minutes = 30;
                break;
            case RecentTimePeriodType.LastHour:
                minutes = 60;
                break;
            case RecentTimePeriodType.Last12Hours:
                minutes = 720;
                break;
            case RecentTimePeriodType.Last24Hours:
                minutes = 1440;
                break;
        }

        const difference = now.getMinutes() - minutes;
        let start = new Date();
        start.setMinutes(difference);

        const request: LogDataRequest = {
            startDate: start,
            endDate: now
        };

        getLogDataInTimePeriod(request);
    }

    function getLogDataByStartAndEndTime() {
        const request: LogDataRequest = {
            startDate: beginSearchDate,
            endDate: endSearchDate
        };

        getLogDataInTimePeriod(request);
    }

    function onSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            setSelectedLogDatum(e.selectedRowsData[0]);
        }
    }

    function onTimePeriodSelectionChanged(e: any) {
        setSelectedTimePeriod(e.value);
    }

    function onDateValueChanged(e: any) {
        if (e.element && e.element.id) {
            if (e.element.id === "beginSearchDate")
                setBeginSearchDate(e.value);

            else if (e.element.id === "endSearchDate")
                setEndSearchDate(e.value);
        }
    }

    async function getLogData() {
        await CrudFunctions.getData(`logging/logdata`)
            .then(data => {
                setLogData(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Log Data");
            });
    }

    async function getLogDataInTimePeriod(request: LogDataRequest) {
        await CrudFunctions.postData("logging/logdatabetweentimeperiod", request)
            .then(data => {
                setLogData(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Log Data");
            });
    }

    //navigation

    function goBack() {
        history.back();
    }

    //render

    function messageItemRender(e: any) {
        if (e.data && e.data.exception)
            return (<div className="invalid-data">{e.data.message}</div>);
        else
            return (<div className="valid-data">{e.data.message}</div>);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

}